import React, { useState } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { Sparkles, Calendar, ArrowRight } from "lucide-react";
import { api } from "../../services/api";
import { setStoredAstriaJapanDob } from "../../utils/astriaJapanKyusei";

// ─── Astria Japan Companion — Inner-Space Form ──────────────────────────
// This is the "quietest" screen in JP lane.
// No images, no icons. One soft card, lots of white space.

const JP_FONT = "'Noto Sans JP', sans-serif";
const C = {
  bg: "linear-gradient(160deg, #FFFDFB 0%, #FBF3EF 100%)",
  card: "#FFFFFF",
  section: "#FBF3EF",
  cardBorder: "#F3E4DC",
  accent: "#D4735D",
  accentSoft: "rgba(212,115,93,0.12)",
  accentGradient: "linear-gradient(135deg, #D4735D 0%, #E0796B 100%)",
  titleColor: "#2B211D",
  bodyColor: "#4A3B33",
  mutedColor: "#8A7469",
  error: "#B4574A",
};

// ─── Date of Birth formatting helpers ─────────────────────────────────
const formatDateValue = (value: string): string => {
  const digits = value.replace(/\D/g, "");
  const limitedDigits = digits.slice(0, 8);
  let formatted = "";
  for (let i = 0; i < limitedDigits.length; i++) {
    if (i === 2 || i === 4) formatted += "/";
    formatted += limitedDigits[i];
  }
  return formatted;
};

const validateDateValue = (value: string): string => {
  if (!value) return value;
  const parts = value.split("/");
  if (parts.length === 3) {
    let day = parseInt(parts[0], 10);
    let month = parseInt(parts[1], 10);
    let year = parseInt(parts[2], 10);
    if (isNaN(day)) day = 1;
    if (day < 1) day = 1;
    if (day > 31) day = 31;
    if (isNaN(month)) month = 1;
    if (month < 1) month = 1;
    if (month > 12) month = 12;
    if (isNaN(year)) year = new Date().getFullYear();
    if (year < 1900) year = 1900;
    if (year > 2020) year = 2020;
    return `${String(day).padStart(2, "0")}/${String(month).padStart(2, "0")}/${year}`;
  }
  return value;
};

const getPreferredLanguage = (): "ja" | "en" => {
  try {
    const lang = localStorage.getItem("preferredLanguage") || "ja";
    return lang === "en" ? "en" : "ja";
  } catch {
    return "ja";
  }
};

const JPCompanionForm: React.FC = () => {
  const navigate = useNavigate();
  const language = getPreferredLanguage();
  const lang = language === "en" ? "en" : "ja";

  // Language-specific content
  const content = {
    ja: {
      brand: "インナー・スペース",
      title: "心の奥へ、そっと。",
      subtext:
        "生年月日を入力して、今日のインナー・スペースを開いてみましょう。",
      dobLabel: "生年月日",
      dobPlaceholder: "DD / MM / YYYY",
      dobRequiredError: "生年月日を正しく入力してください。",
      genericError: "うまく取得できませんでした。もう一度お試しください。",
      submitButton: "インナー・スペースを開く",
      loadingButton: "静かに整えています…",
      footer: "あなたの心に、そっと寄り添う時間を。",
      hint: "あなたの心の奥の静かな流れを感じるために使用します。",
    },
    en: {
      brand: "Inner Space",
      title: "Quietly, into your heart.",
      subtext: "Enter your date of birth to open today's Inner Space.",
      dobLabel: "Date of Birth",
      dobPlaceholder: "DD / MM / YYYY",
      dobRequiredError: "Please enter a valid date of birth.",
      genericError: "Something went wrong. Please try again.",
      submitButton: "Open Inner Space",
      loadingButton: "Quietly preparing…",
      footer: "A gentle moment for your heart.",
      hint: "Used to sense the quiet flow within your heart.",
    },
  };

  const t = content[lang];

  const [dob, setDob] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async () => {
    if (!dob || dob.split("/").length !== 3) {
      setError(t.dobRequiredError);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const response = await api.post(
        "/backend/astria-japan-kyusei/companion",
        {
          dob,
        },
      );
      if (response.data?.success) {
        sessionStorage.setItem(
          "astriaJapanCompanion",
          JSON.stringify(response.data),
        );
        setStoredAstriaJapanDob(dob);
        navigate("/astria-japan/companion/result");
      } else {
        setError(response.data?.message || t.genericError);
      }
    } catch (err: any) {
      setError(err?.response?.data?.message || t.genericError);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center px-4 py-10"
      style={{ background: C.bg, fontFamily: JP_FONT }}
    >
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: "easeOut" }}
        className="w-full"
        style={{
          background: C.card,
          borderRadius: 20,
          boxShadow: "0 12px 40px rgba(0,0,0,0.06), 0 2px 8px rgba(0,0,0,0.02)",
          maxWidth: 480,
          padding: 36,
        }}
      >
        {/* ─── Brand ────────────────────────────────────────────────────── */}
        <div className="flex items-center gap-3 mb-4">
          <div
            style={{
              width: 40,
              height: 40,
              borderRadius: "50%",
              background: C.accentGradient,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              boxShadow: `0 4px 16px ${C.accent}44`,
            }}
          >
            <Sparkles className="w-5 h-5" style={{ color: "#FFFFFF" }} />
          </div>
          <div
            style={{
              fontSize: 13,
              fontWeight: 500,
              color: C.mutedColor,
              letterSpacing: "0.1em",
            }}
          >
            {t.brand}
          </div>
        </div>

        {/* ─── Title ────────────────────────────────────────────────────── */}
        <div
          style={{
            fontSize: 24,
            fontWeight: 600,
            color: C.titleColor,
            marginBottom: 4,
            lineHeight: 1.3,
          }}
        >
          {t.title}
        </div>
        <p
          style={{
            fontSize: 14,
            color: C.mutedColor,
            marginBottom: 28,
            lineHeight: 1.7,
          }}
        >
          {t.subtext}
        </p>

        {/* ─── Form Section ────────────────────────────────────────────── */}
        <div style={{ background: C.section, borderRadius: 16, padding: 24 }}>
          <div>
            <label
              className="block text-[11px] font-semibold tracking-[0.15em] uppercase mb-1.5"
              style={{ color: C.mutedColor }}
            >
              {t.dobLabel}
              <span className="ml-1" style={{ color: C.accent }}>
                *
              </span>
            </label>
            <div className="relative">
              <Calendar
                className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4"
                style={{ color: C.mutedColor }}
              />
              <input
                type="text"
                inputMode="numeric"
                placeholder={t.dobPlaceholder}
                value={dob}
                onChange={(e) => {
                  const rawValue = e.target.value;
                  setDob(formatDateValue(rawValue));
                }}
                onBlur={(e) => {
                  setDob(validateDateValue(e.target.value));
                  e.currentTarget.style.borderColor = C.cardBorder;
                  e.currentTarget.style.boxShadow = "none";
                }}
                className="w-full rounded-full border pl-11 pr-4 py-3 text-[15px] shadow-sm focus:outline-none focus:ring-1 transition-all duration-200"
                style={{
                  borderColor: C.cardBorder,
                  color: C.titleColor,
                  background: "#FFFFFF",
                  fontFamily: JP_FONT,
                }}
                onFocus={(e) => {
                  e.currentTarget.style.borderColor = C.accent;
                  e.currentTarget.style.boxShadow = `0 0 0 3px ${C.accentSoft}`;
                }}
              />
            </div>
            <p
              className="mt-2 text-xs"
              style={{ color: C.mutedColor, opacity: 0.7 }}
            >
              {t.hint}
            </p>
          </div>
        </div>

        {/* ─── Error Message ───────────────────────────────────────────── */}
        {error && (
          <motion.p
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            className="mt-4 text-sm flex items-center gap-2"
            style={{ color: C.error }}
          >
            <span className="text-base">✧</span>
            {error}
          </motion.p>
        )}

        {/* ─── Submit Button ───────────────────────────────────────────── */}
        <motion.button
          onClick={handleSubmit}
          disabled={loading}
          className="mt-6 w-full rounded-full py-3.5 text-sm font-semibold text-white transition-all duration-200 flex items-center justify-center gap-2 group"
          style={{
            background: C.accentGradient,
            boxShadow: `0 4px 16px ${C.accent}44`,
            opacity: loading ? 0.6 : 1,
          }}
          whileHover={{ scale: loading ? 1 : 1.02 }}
          whileTap={{ scale: loading ? 1 : 0.98 }}
        >
          {loading ? (
            <>
              <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              {t.loadingButton}
            </>
          ) : (
            <>
              {t.submitButton}
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </>
          )}
        </motion.button>

        {/* ─── Footer ───────────────────────────────────────────────────── */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4 }}
          style={{
            textAlign: "center",
            marginTop: 24,
            paddingTop: 20,
            borderTop: "1px solid rgba(240,232,228,0.3)",
          }}
        >
          <div
            style={{
              fontSize: 11,
              color: C.mutedColor,
              opacity: 0.7,
              letterSpacing: "0.05em",
            }}
          >
            {t.footer}
          </div>
        </motion.div>
      </motion.div>
    </div>
  );
};

export default JPCompanionForm;
